import { createCourseImageRecovery } from './28-image-sources.mjs';
import { MEDIA_LIMITS } from '../../../packages/domain/src/04-media.mjs';
/** Inspect a post's ordered media with native swipe scrolling and video controls.
 * Each opening owns and cleans up its modal/browser listeners; authored captions
 * and alt text remain literal. Playback never escapes the selected visible page. */
export function createMediaViewer(
  ctx,
  {
    doc = globalThis.document,
    frame = globalThis.requestAnimationFrame ?? ((callback) => callback()),
    motion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)'),
    delay = globalThis.setTimeout,
    cancelDelay = globalThis.clearTimeout,
    network = globalThis.navigator?.connection,
    imageRecovery = createCourseImageRecovery(ctx),
  } = {},
) {
  let cleanup;
  function open(post, startIndex = 0) {
    cleanup?.();
    const supplied =
      Array.isArray(post.media) && post.media.length
        ? post.media
        : [
            {
              kind: 'image',
              url: ctx.photoSource(post.image),
              alt: post.alt || ctx.uiText('Shared photo'),
            },
          ];
    // The server owns the media limit; defensive presentation keeps malformed
    // persisted arrays from creating unbounded nodes or losing a clicked index.
    const media = supplied
      .slice(0, MEDIA_LIMITS.photos)
      .flatMap((item, sourceIndex) =>
        item && ['image', 'video'].includes(item.kind) && typeof item.url === 'string' && item.url
          ? [{ ...item, sourceIndex }]
          : [],
      );
    if (!media.length) return;
    const selected = media.findIndex((item) => item.sourceIndex === startIndex);
    const initial = selected >= 0 ? selected : 0;
    let current = initial,
      target = null,
      timer,
      disposed = false,
      queued = false;
    const body = ctx.e('div', 'media-viewer lightbox');
    const track = ctx.e('div', 'media-viewer-track');
    track.tabIndex = 0;
    track.setAttribute('aria-label', ctx.uiText('Media carousel'));
    track.setAttribute('role', 'region');
    const videos = new Map();
    const images = new Map();
    const videoListeners = [];
    const imageDisposers = [];
    media.forEach((item, index) => {
      const page = ctx.e('div', 'media-viewer-page');
      page.dataset.mediaIndex = String(index);
      page.setAttribute('role', 'group');
      page.setAttribute('aria-label', `${index + 1} / ${media.length}`);
      if (item.kind === 'video') {
        const video = ctx.field('video', {
          controls: true,
          muted: true,
          defaultMuted: true,
          playsInline: true,
          preload: 'none',
        });
        video.setAttribute('aria-label', item.alt || post.title || ctx.uiText('Shared video'));
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        const onPlay = () => {
          if (disposed || doc.hidden || index !== current) video.pause();
          else
            for (const other of doc.querySelectorAll('video')) if (other !== video) other.pause();
        };
        video.addEventListener('play', onPlay);
        videoListeners.push([video, onPlay]);
        videos.set(index, video);
        page.append(video);
      } else {
        const image = ctx.field('img', {
          alt: item.alt || post.title || ctx.uiText('Shared photo'),
          decoding: 'async',
        });
        if (!post.media?.length)
          imageDisposers.push(
            imageRecovery.bind(image, post, {
              onRecovered: (current) => {
                post = current;
                media[index].url = ctx.photoSource(current.image);
              },
            }),
          );
        images.set(index, image);
        page.append(image);
      }
      track.append(page);
    });
    const controls = ctx.e('div', 'media-viewer-controls');
    const previous = ctx.iconButton('arrow-left', 'Previous media', () =>
      move((target ?? current) - 1),
    );
    const next = ctx.iconButton('chevron-right', 'Next media', () => move((target ?? current) + 1));
    const position = ctx.e('span', 'media-viewer-count');
    position.setAttribute('aria-live', 'polite');
    position.setAttribute('aria-atomic', 'true');
    controls.append(previous, position, next);
    const pagination = ctx.e('div', 'media-viewer-pagination');
    pagination.setAttribute('aria-label', ctx.uiText('Choose media'));
    const dots = media.map((_item, index) => {
      const button = ctx.button('', () => move(index), 'media-viewer-position');
      button.setAttribute(
        'aria-label',
        `${ctx.uiText('View media')} ${index + 1} / ${media.length}`,
      );
      pagination.append(button);
      return button;
    });
    function loadVisibleMedia() {
      if (disposed || doc.hidden) return;
      const selectedImage = images.get(current);
      const selectedVideo = videos.get(current);
      if (
        selectedImage &&
        !selectedImage.src &&
        selectedImage.dataset.imageRecovery !== 'unavailable'
      )
        selectedImage.src = media[current].url;
      if (selectedVideo && !selectedVideo.src) {
        selectedVideo.preload = 'metadata';
        selectedVideo.src = media[current].url;
      }
      // One next public image is the entire speculative budget. Browser HTTP
      // cache owns reuse/eviction; no private bytes or persistent browser store
      // are introduced. Existing in-flight img requests cannot always be canceled.
      const next = current + 1;
      if (network?.saveData || ['slow-2g', '2g'].includes(network?.effectiveType)) return;
      const image = images.get(next);
      if (
        image &&
        !image.src &&
        /^\/assets\/[a-zA-Z0-9/_-]+\.(?:webp|png|jpe?g|svg)$/.test(media[next].url)
      )
        image.src = media[next].url;
    }
    function display(index) {
      position.textContent = `${index + 1} / ${media.length}`;
      previous.setAttribute('aria-disabled', String(index === 0));
      next.setAttribute('aria-disabled', String(index === media.length - 1));
      dots.forEach((button, at) => button.setAttribute('aria-current', String(at === index)));
    }
    function pauseOthers() {
      for (const [index, video] of videos)
        if (disposed || doc.hidden || index !== current) video.pause();
    }
    function move(index, behavior = motion?.matches ? 'instant' : 'smooth') {
      if (index < 0 || index >= media.length) return;
      target = Math.max(0, Math.min(media.length - 1, index));
      current = target;
      loadVisibleMedia();
      pauseOthers();
      display(target);
      track.scrollTo({ left: target * track.clientWidth, behavior });
    }
    function readPosition() {
      if (disposed || !track.clientWidth) return;
      current = Math.max(
        0,
        Math.min(media.length - 1, Math.round(track.scrollLeft / track.clientWidth)),
      );
      loadVisibleMedia();
      pauseOthers();
      if (target === null) display(current);
    }
    function settled() {
      target = null;
      readPosition();
      display(current);
    }
    function onScroll() {
      if (!queued) {
        queued = true;
        frame(() => {
          queued = false;
          readPosition();
        });
      }
      cancelDelay(timer);
      timer = delay(settled, 120);
    }
    function onKey(event) {
      // Keep native video volume/seeking controls intact.
      if (event.target?.tagName?.toLowerCase() === 'video') return;
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move((target ?? current) + (event.key === 'ArrowRight' ? 1 : -1));
    }
    track.addEventListener('scroll', onScroll);
    track.addEventListener('scrollend', settled);
    body.addEventListener('keydown', onKey);
    body.append(track);
    if (media.length > 1) body.append(controls, pagination);
    body.append(
      ctx.e('p', 'media-viewer-caption', post.text),
      ctx.link(ctx.uiText('Open post and replies'), `#/post/${post.id}`, 'secondary-button'),
    );
    // Opening inspection pauses underlying feed media. Feed playback remains
    // manual until its own next visibility transition, avoiding competing audio.
    for (const video of doc.querySelectorAll('video')) video.pause();
    ctx.dialog(media.length === 1 && media[0].kind !== 'video' ? 'Photo' : 'Media', body);
    const modal = ctx.modal ?? doc.querySelector('#modal');
    const visibility = () => {
      loadVisibleMedia();
      pauseOthers();
    };
    doc.addEventListener('visibilitychange', visibility);
    const close = () => cleanup?.();
    modal?.addEventListener('close', close, { once: true });
    const resize = () => {
      if (!disposed) move(target ?? current, 'instant');
    };
    globalThis.addEventListener?.('resize', resize);
    cleanup = () => {
      disposed = true;
      for (const dispose of imageDisposers) dispose();
      cancelDelay(timer);
      pauseOthers();
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', settled);
      body.removeEventListener('keydown', onKey);
      doc.removeEventListener('visibilitychange', visibility);
      modal?.removeEventListener('close', close);
      globalThis.removeEventListener?.('resize', resize);
      for (const [video, listener] of videoListeners) video.removeEventListener('play', listener);
      cleanup = undefined;
    };
    frame(() => {
      if (!disposed) {
        move(initial, 'instant');
        track.focus({ preventScroll: true });
      }
    });
    return body;
  }
  return { open, dispose: () => cleanup?.() };
}
